// The v2 practical-lesson task machine: one `evaluateTick` per frame turns the car's state
// into the events the instructor strip, the scripted traffic and the finish flow react to.
// Pure — no Phaser, no DOM, no config: the tasks come in from `lessonScript.buildTasks()`
// and the world (pedestrian, lights, side-road car…) is described by `input.actors`, which
// the scene fills in. RouteDirector (Task 7) is the only thing that wires the two together.
//
// The car drives LEFT, so x decreases: a task spans `[x0, x1]` with `x0 >= x1`, is
// announced at `x0` and decided at `x1`.
//
// `input.x` is the car sprite's CENTRE in world px (Task 7 feeds `car.sprite.x`). The line
// rules — the stop/give-way bar, the zebra, the traffic light's line, and the continuous
// line's zone — judge the car's FRONT instead: `x ∓ carWidth / 2` on the side it is facing,
// because the bonnet is what reaches a line first and what a stop has to keep behind it.
// `finish` and `limit` still go by the centre (nothing is being stopped AT), and `park`
// measures the whole footprint as `x ± carWidth / 2`.

import { TASK_COPY_PL } from '../scenes/lessonScript.mjs';
import { correctLane } from './laneLogic.mjs';

const PX_PER_M = 100;           // the strip counts down in metres
const SPEED_TOLERANCE = 3;      // km/h over a limit sign that still counts as obeying it
const YIELD_SLOW_KMH = 15;      // "slowed down enough to give way"
const LANE_GRACE_S = 3;         // seconds on the wrong lane before it's a mistake
const PARKED_GO_S = 3;          // seconds stopped behind the parked car before it pulls away
const PARK_TOLERANCE = 20;      // px a parked car may stick out of its bay
const TRIGGER_AHEAD = 900;      // px before a light where its sequence is started
const SIDE_TRIGGER_AHEAD = 1400; // px before a junction where its side-road car sets off (it then waits at the mouth)
const CROSSING_BAND = 700;      // px before the zebra where stopping counts as letting them cross

// Where a task stops being an announcement and starts being judged — what the strip shows
// as „active", and for several kinds the exact stretch the rule looks at.
const ACTIVATE_X = {
  limit: (t) => t.x0,
  stop: (t) => t.meta.window[0],
  yield: (t) => t.meta.window[0],
  crossing: (t) => t.meta.zebra.x0 + CROSSING_BAND,
  light: (t) => stopLineOf(t) + 300,
  oncoming: (t) => t.meta.trigger,
};

// The kinds a line is judged against the car's FRONT for (see the header): everything you
// have to stop at, plus the continuous line, whose no-overtaking zone begins the moment the
// bonnet is on it. Everything else — `limit`, `finish`, `park`, `lane` — keeps the centre.
const FRONT_JUDGED = new Set(['stop', 'yield', 'crossing', 'light', 'solid']);

const stopLineOf = (task) => task.meta.stopLine ?? task.x1;
const failTextFor = (kind) => TASK_COPY_PL[kind]?.fail ?? null;
const okTextFor = (kind) => TASK_COPY_PL[kind]?.ok ?? null;
const distanceM = (task, x) => Math.max(0, Math.round((x - task.x1) / PX_PER_M));

// Tasks that only count as passed if they actually resolved: getting to the end of the
// route is the point of the drive, not a formality nobody had to act on.
const MUST_RESOLVE = new Set(['park', 'finish']);

/**
 * A fresh run over `tasks` (from `buildTasks`). `mode` is 'lesson' or 'exam' — the only
 * difference is that an exam stops dead on the first major mistake, while a lesson always
 * drives to the end and reports every task at the finish.
 */
export function createRun(tasks, { mode = 'lesson' } = {}) {
  const run = {
    mode,
    // The `lane` task is background — it has no announcement, it's live from the first frame.
    tasks: tasks.map((t) => ({ ...t, status: t.kind === 'lane' ? 'active' : 'pending', flags: {} })),
    finished: false,
    failedMajor: false,
    minor: 0,
    major: 0,
    crashes: 0,
    wasCrashed: false,
  };
  // One row per task, in driving order — exactly what `lesson-finish`/`practical-finish`
  // want. A task the run never reached (an exam that ended early, a sign nobody had to act
  // on) counts as passed: a checklist of things that never happened would read as failures.
  // The two exceptions are `park` and `finish` — reaching the end of the route IS the task,
  // so an unresolved one means the run never got there.
  run.results = () => run.tasks.map(({ id, kind, severity, status }) => ({
    id, kind, ok: status === 'ok' || (status !== 'fail' && !MUST_RESOLVE.has(kind)), severity,
  }));
  return run;
}

function push(events, event) {
  events.push(event);
  return event;
}

function resolveOk(run, task, events) {
  task.status = 'ok';
  push(events, { type: 'ok', task, text: okTextFor(task.kind) });
}

// A mistake. `severity` comes from the task (the server re-derives it from `kind` anyway);
// `crash` passes its own because it isn't a task the route ever planned for.
function resolveFail(run, task, events, { severity = task.severity, text } = {}) {
  task.status = 'fail';
  task.flags.fails = (task.flags.fails ?? 0) + 1;
  if (severity === 'minor') run.minor += 1;
  if (severity === 'major') run.major += 1;
  push(events, { type: 'fail', task, severity, text: text ?? failTextFor(task.kind) });
  if (severity !== 'major') return;
  run.failedMajor = true;
  // Exam: the first major ends it there and then. A lesson always drives to the finish —
  // the child sees the ❌ on the strip and carries on with the next task.
  if (run.mode === 'exam') finish(run, events, true);
}

function finish(run, events, failed) {
  run.finished = true;
  push(events, { type: 'finish', failed });
}

function need(events, task, what) {
  push(events, { type: 'need', task, what });
}

// Announce at x0, then count the metres down every frame until the task resolves. The strip
// throttles the rendering — the machine just says where things stand. Returns false while
// the task is still ahead of the car, which is the whole of "no rule applies yet".
function announced(task, x, events) {
  if (task.status === 'pending') {
    if (x > task.x0) return false;
    task.status = 'announced';
    push(events, { type: 'announce', task, distanceM: distanceM(task, x), hint: task.hint ?? null });
    return true;
  }
  push(events, { type: 'progress', task, distanceM: distanceM(task, x) });
  return true;
}

function activate(task, x, events) {
  if (task.status !== 'announced') return;
  const at = ACTIVATE_X[task.kind] ?? ((t) => t.x0);
  if (x > at(task)) return;
  task.status = 'active';
  push(events, { type: 'activate', task });
}

// ── the rules ──────────────────────────────────────────────────────────────────
// Each handler sees a task that has already been announced, and runs until the task
// resolves (`ok`/`fail`) — after which the tick loop skips it for the rest of the run.

// From the sign to the end of the segment, `v <= limit + 3`. The task is ANNOUNCED earlier
// (`x0`) than it is enforced (`meta.signX`) so braking has somewhere to happen. One mistake
// per sign: the task resolves the moment it's broken, so a long stretch of speeding is
// still one minor.
function tickLimit(run, task, inp, events) {
  const from = task.meta.signX ?? task.x0;
  if (inp.x <= from && inp.v > task.meta.limit + SPEED_TOLERANCE) {
    resolveFail(run, task, events);
    return;
  }
  if (inp.x <= task.x1) resolveOk(run, task, events);
}

// A full stop inside the window before the line, and the side-road car through the junction
// before we pull away.
function tickStop(run, task, inp, events) {
  triggerSide(task, inp, events);
  const [w0, w1] = task.meta.window;
  if (inp.front <= w0 && inp.front > w1) {
    if (inp.v === 0) task.flags.stopped = true;
    if (!task.flags.stopped) need(events, task, 'stop');
    else if (!inp.actors.sideRoadPassed) need(events, task, 'wait-side');
  }
  if (inp.front > task.x1) return;
  if (task.flags.stopped && inp.actors.sideRoadPassed) resolveOk(run, task, events);
  else resolveFail(run, task, events);
}

// Give way: slow to a crawl in the window, and let the main-road car past first.
function tickYield(run, task, inp, events) {
  triggerSide(task, inp, events);
  const [w0, w1] = task.meta.window;
  if (inp.front <= w0 && inp.front > w1) {
    if (inp.v <= YIELD_SLOW_KMH) task.flags.slowed = true;
    if (!task.flags.slowed) need(events, task, 'stop');
    else if (!inp.actors.sideRoadPassed) need(events, task, 'wait-side');
  }
  if (inp.front > task.x1) return;
  if (task.flags.slowed && inp.actors.sideRoadPassed) resolveOk(run, task, events);
  else resolveFail(run, task, events);
}

// The side-road car waits hidden until the player is close enough to see it pull out.
function triggerSide(task, inp, events) {
  if (task.flags.triggeredSide || inp.x > task.x1 + SIDE_TRIGGER_AHEAD) return;
  task.flags.triggeredSide = true;
  push(events, { type: 'trigger-side', task });
}

// The child at the kerb only steps out once the car has actually stopped before the zebra;
// crossing the far edge while they're waiting or on the road is the mistake.
function tickCrossing(run, task, inp, events) {
  const { zebra } = task.meta;
  const { pedestrian } = inp.actors;
  const inTheWay = pedestrian === 'waiting' || pedestrian === 'crossing';
  if (!task.flags.pedestrianGo && inp.v === 0 && pedestrian === 'waiting'
      && inp.front <= zebra.x0 + CROSSING_BAND && inp.front >= zebra.x1 + 40) {
    task.flags.pedestrianGo = true;
    push(events, { type: 'pedestrian-go', task });
  }
  if (inp.front <= zebra.x0 + CROSSING_BAND && inp.front > zebra.x1 && inTheWay) need(events, task, 'wait-pedestrian');
  if (inp.front > zebra.x1) return;
  if (inTheWay) resolveFail(run, task, events);
  else resolveOk(run, task, events);
}

// The light sequence is the scene's (red 4 s → red+yellow 1,5 s → green); the rule only
// asks what colour was showing as the car crossed the line.
function tickLight(run, task, inp, events) {
  if (!task.flags.triggeredLight && inp.x <= task.meta.lightPole + TRIGGER_AHEAD) {
    task.flags.triggeredLight = true;
    push(events, { type: 'trigger-light', task });
  }
  const line = stopLineOf(task);
  const stopping = inp.actors.light === 'red' || inp.actors.light === 'redyellow';
  if (inp.front <= line + 300 && inp.front > line && stopping) need(events, task, 'wait-light');
  if (inp.front > line) return;
  if (stopping) resolveFail(run, task, events);
  else resolveOk(run, task, events);
}

// A continuous line runs the whole segment: no lane change at all, however slow the car in
// front is. Stopping behind it for 3 s is what makes it drive off (the scene's `parked-go`).
function tickSolid(run, task, inp, events) {
  // The zone begins as soon as the BONNET is on the continuous line (this handler only runs
  // from the frame `inp.front` passes the task's x0 — see FRONT_JUDGED). A change already in
  // flight by then was started on the dashed line behind us — seeding `wasChanging` on the
  // first evaluated tick keeps it from reading as a rising edge here. A change STARTED
  // inside the zone is the mistake.
  const wasChanging = task.flags.wasChanging ?? inp.laneChanging;
  const startedHere = inp.laneChangedThisFrame || (inp.laneChanging && !wasChanging);
  task.flags.wasChanging = inp.laneChanging;
  if (startedHere) {
    resolveFail(run, task, events);
    return;
  }
  if (inp.actors.parkedCarAhead && !inp.actors.parkedCarGone) {
    if (inp.v === 0) task.flags.stoppedS = (task.flags.stoppedS ?? 0) + inp.dt;
    if (!task.flags.parkedGo && (task.flags.stoppedS ?? 0) >= PARKED_GO_S) {
      task.flags.parkedGo = true;
      push(events, { type: 'parked-go', task });
    }
    need(events, task, 'follow');
  }
  if (inp.x <= task.x1) resolveOk(run, task, events);
}

// Where the line goes dashed: pull out (↓), get past the car, get back in (↑) before the
// window ends. Back in lane AHEAD of the car (`actors.parkedCarX`, when the scene knows it)
// resolves it on the spot; back in lane still behind it is not an overtake — the flags reset
// and the child can have another go while the window lasts.
const OVERTAKE_PAST_PX = 150;   // how far the car's centre must be behind ours to count as passed

function tickOvertake(run, task, inp, events) {
  const mine = correctLane(inp.facing);
  const carX = inp.actors.parkedCarX;
  if (task.flags.lastLane === undefined) task.flags.lastLane = inp.lane;
  if (inp.lane !== task.flags.lastLane) {
    if (inp.lane !== mine) task.flags.pulledOut = true;
    else if (task.flags.pulledOut) {
      const past = carX == null || inp.x < carX - OVERTAKE_PAST_PX;
      if (past) task.flags.cameBack = true;
      else task.flags.pulledOut = false;   // tucked back in behind it: not done, try again
    }
    task.flags.lastLane = inp.lane;
  }
  if (task.flags.cameBack) { resolveOk(run, task, events); return; }
  if (inp.x > task.x1) return;
  // Never pulled out at all: overtaking is an opportunity, not an order — staying behind
  // the car in front is a perfectly good drive. The task resolves ok with nothing to say,
  // so the strip goes back to resting instead of celebrating or scolding.
  if (!task.flags.pulledOut) {
    task.status = 'ok';
    push(events, { type: 'ok', task, text: null });
    return;
  }
  resolveFail(run, task, events);
}

// The oncoming car runs on the near lane; the scene decides whether it was clipped.
function tickOncoming(run, task, inp, events) {
  if (!task.flags.triggered && inp.x <= task.meta.trigger) {
    task.flags.triggered = true;
    push(events, { type: 'trigger-oncoming', task });
  }
  if (inp.actors.oncomingHit) { resolveFail(run, task, events); return; }
  if (inp.x <= task.x1) resolveOk(run, task, events);
}

// Information only — „zakaz wjazdu, jedziemy prosto". There is nothing to get wrong.
function tickNoentry(run, task, inp, events) {
  if (inp.x <= task.x1) resolveOk(run, task, events);
}

// The whole car between one bay's lines (±20 px), stopped, IN the lay-by (lane 'bay' — two
// presses down from your own lane, which is what makes parking a manoeuvre: ↓↓, line up,
// stop). That ends the route. Driving past the car park without parking is the mistake, and
// the route ends there too — the road does not go on.
function tickPark(run, task, inp, events) {
  if (inp.x <= task.x1) {
    resolveFail(run, task, events);
    if (!run.finished) finish(run, events, false);
    return;
  }
  if (inp.v !== 0) { task.flags.asked = false; return; }
  const half = (inp.carWidth ?? 0) / 2;
  const inBay = task.meta.bays.some((bay) => inp.x - half >= bay.x1 - PARK_TOLERANCE
    && inp.x + half <= bay.x0 + PARK_TOLERANCE);
  const besideBays = inp.lane === 'bay';
  if (inBay && besideBays) {
    resolveOk(run, task, events);
    finish(run, events, false);
    return;
  }
  if (task.flags.asked) return;   // once per stop — standing still isn't news every frame
  task.flags.asked = true;
  need(events, task, besideBays ? 'park' : 'park-lane');
}

// Routes without a car park end at ROUTE_FINISH_X.
function tickFinish(run, task, inp, events) {
  if (inp.x > task.x1) return;
  resolveOk(run, task, events);
  finish(run, events, false);
}

// Background: stay on your own lane. Overtaking is the one legitimate reason to be on the
// other one, so an open `overtake` window suspends the clock. Three seconds outside it is
// one minor; the clock then restarts, so a second long stretch is a second mistake — but
// the task's result is already ❌ from the first one.
function tickLane(run, task, inp, events) {
  const wrong = inp.lane !== correctLane(inp.facing);
  const overtaking = run.tasks.some((t) => t.kind === 'overtake'
    && (t.status === 'announced' || t.status === 'active')
    && inp.x <= t.x0 && inp.x > t.x1);
  // Parking is done from the near lane, so once the car park is announced the lane rule
  // stands down — pulling in is the exercise, not a wrong lane.
  const parking = run.tasks.some((t) => t.kind === 'park'
    && (t.status === 'announced' || t.status === 'active') && inp.x <= t.x0);
  if (!wrong || overtaking || parking) { task.flags.wrongS = 0; return; }
  task.flags.wrongS = (task.flags.wrongS ?? 0) + inp.dt;
  need(events, task, 'lane-back');
  if (task.flags.wrongS <= LANE_GRACE_S) return;
  task.flags.wrongS = 0;
  resolveFail(run, task, events);
}

const HANDLERS = {
  limit: tickLimit, stop: tickStop, yield: tickYield, crossing: tickCrossing,
  light: tickLight, solid: tickSolid, overtake: tickOvertake, oncoming: tickOncoming,
  noentry: tickNoentry, park: tickPark, finish: tickFinish, lane: tickLane,
};

// A collision isn't a task the route planned for, so it gets a synthetic one appended to
// the results — named after the segment it happened in, so the summary reads sensibly.
function tickCrash(run, inp, events) {
  if (!inp.actors.crashed) { run.wasCrashed = false; return; }
  if (run.wasCrashed) return;
  run.wasCrashed = true;
  run.crashes += 1;
  const here = run.tasks.filter((t) => t.kind !== 'lane' && t.kind !== 'crash' && t.x0 >= inp.x);
  const prefix = here.length ? here[here.length - 1].id.split('-').slice(0, 2).join('-') : 'route';
  const task = {
    id: `${prefix}-crash-${run.crashes}`,
    kind: 'crash',
    x0: inp.x, x1: inp.x,
    severity: 'major',
    title: failTextFor('crash') ?? '',
    hint: null,
    icon: null,
    meta: {},
    status: 'active',
    flags: {},
  };
  run.tasks.push(task);
  resolveFail(run, task, events);
}

const DEFAULT_ACTORS = {
  sideRoadPassed: false, pedestrian: 'none', light: 'off',
  parkedCarAhead: false, parkedCarGone: false, oncomingHit: false, crashed: false,
};

/**
 * One frame of driving.
 *
 * `input`: `{ x, v, lane, laneChanging, laneChangedThisFrame, facing, dt, carWidth, actors }`
 * — `v` in km/h, `x` in world px (the sprite's CENTRE), `dt` in seconds, `lane`/`facing`
 * 'left'|'right', `carWidth` the car's world width in px (the bonnet's offset from the
 * centre, and the footprint `park` measures), `actors` the scripted world's state.
 *
 * Returns the events raised this frame, in task order (the order they're driven into).
 * A finished run returns nothing at all — including every frame after an exam's first
 * major mistake, which is what "the exam ends there" means for the caller.
 */
export function evaluateTick(run, input) {
  if (run.finished) return [];
  // A missing field degrades rather than poisoning the run: no `dt` means no timer moves,
  // no `facing` means the default direction of travel — never NaN, never a permanent
  // "wrong lane".
  const facing = input.facing ?? 'left';
  const half = (input.carWidth ?? 0) / 2;
  const inp = {
    ...input,
    dt: input.dt ?? 0,
    facing,
    // The bonnet, once per frame: what the line rules judge (see FRONT_JUDGED).
    front: facing === 'left' ? input.x - half : input.x + half,
    actors: { ...DEFAULT_ACTORS, ...(input.actors ?? {}) },
  };
  const events = [];
  tickCrash(run, inp, events);
  if (run.finished) return events;
  for (const task of run.tasks) {
    // A resolved task is done — except `lane`, which is live for the whole route and can
    // go wrong again after it has already been marked ❌.
    if (task.status === 'ok' || (task.status === 'fail' && task.kind !== 'lane')) continue;
    if (task.kind === 'lane') {
      tickLane(run, task, inp, events);
    } else {
      // Announced, counted down and activated from whichever end of the car its rule
      // measures — a stop bar is 3 m away when the BONNET is 3 m away from it.
      const px = FRONT_JUDGED.has(task.kind) ? inp.front : inp.x;
      if (!announced(task, px, events)) continue;
      activate(task, px, events);
      HANDLERS[task.kind]?.(run, task, inp, events);
    }
    if (run.finished) break;
  }
  return events;
}
